import * as _ from 'lodash-es';
import Container from 'quill/blots/container';
import Quill, { Delta } from 'quill/core';
import type { Range } from 'quill/core';
import { TableCell } from 'quill/formats/table';
import { isEditorCompatibleImage } from './editorImageMimeTypes';
import Attachment from './formats/Attachment';
import Block from './formats/Block';
import ImageBlot from './formats/ImageBlot';
import getDropPlaceholderPosition from './getDropPlaceholderPosition';
import type { DraggableBlot } from './ReorderGutterButton';
import type { Blot } from './types';
import * as styles from './Uploader.module.scss';
import getImagePlaceholderInfo, {
  getImageUploadsForFiles,
} from './utils/getImagePlaceholderInfo';
import onDnDDragEnd from './utils/onDnDDragEnd';
import isFileOverlarge from '../utils/network/isFileOverlarge';

const Module = Quill.import('core/module');

export const FILE_OVERSIZED_ERROR = 'FILE_OVERSIZED_ERROR';
export const IMAGE_OVERSIZED_ERROR = 'IMAGE_OVERSIZED_ERROR';

const getRegularFiles = async (dataTransfer: DataTransfer | null) => {
  if (!dataTransfer) return [];
  const files = Array.from(dataTransfer.files);
  if (!files.length) {
    return [];
  }

  if (files.every((file) => file.type)) {
    return files;
  }

  let isDirectoryList: (boolean | undefined)[] | null = null;

  // Inspired by https://stackoverflow.com/a/8857445/1109791
  // @ts-expect-error Feature detection
  if (dataTransfer.items?.[0].webkitGetAsEntry) {
    isDirectoryList = Array.from(dataTransfer.items).map((item) => {
      const entry = item.webkitGetAsEntry();
      return entry?.isDirectory;
    });
  } else {
    const tasks = files.map(
      (file) =>
        new Promise<boolean>((resolve) => {
          const fileReader = new FileReader();
          const fileSample = file.slice(0, 5);
          fileReader.onerror = () => resolve(true);
          fileReader.onload = () => resolve(false);
          fileReader.readAsArrayBuffer(fileSample);
        }),
    );
    isDirectoryList = await Promise.all(tasks);
  }

  return files.filter((_file, index) => !isDirectoryList?.[index]);
};

const isBlotDroppable = (blot: Blot): blot is Attachment =>
  blot.statics.blotName === Attachment.blotName;

function getTableForCell(line: Blot | null) {
  // TableCell is also a valid "line" for `Quill#getLine()`
  // but we don't want it to be the drop target.
  if (line?.statics.blotName === TableCell.blotName) {
    // @ts-expect-error
    return line.table();
  }
  return line;
}

function getLineBlotFromTarget(
  quill: Quill,
  { target, clientY }: { target: Node; clientY: number },
) {
  const blot = quill.scroll.find(target, true);
  if (!blot) {
    return null;
  }
  const index = quill.getIndex(blot);
  let line = typeof index === 'number' ? quill.getLine(index)[0] : null;
  if (!line) {
    return null;
  }

  if (isBlotDroppable(line)) {
    return line;
  }

  // Table cells have their own Uploader
  if (
    line.statics.blotName === 'table-embed' &&
    target instanceof Element &&
    target.closest('td')
  ) {
    return null;
  }

  // Images and carousels are handling dropping themselves
  if (blot instanceof ImageBlot) {
    return null;
  }

  line = getTableForCell(line);

  let placement = 'bottom';
  if (line?.prev) {
    if (
      blot === quill.scroll.children.tail &&
      blot.length() <= 1 &&
      blot.statics.blotName === Block.blotName
    ) {
      placement = 'top';
    } else {
      const { top, bottom } = line.domNode.getBoundingClientRect();
      const middle = (bottom - top) / 2 + top;
      if (clientY < middle) {
        placement = 'top';
      }
    }
  }

  if (placement === 'top') {
    line = line && getTableForCell(line.prev);
  }

  return line;
}

async function embedsFromImageFiles(validFiles: File[]) {
  if (validFiles.length > 1) {
    const uploads = await getImageUploadsForFiles(validFiles);

    return [
      {
        name: ImageBlot.blotName,
        value: ImageBlot.addImageIdsIfNeeded(uploads),
        attributes: {},
      },
    ];
  }

  return (
    await Promise.all(
      validFiles.filter(isEditorCompatibleImage).map(getImagePlaceholderInfo),
    )
  ).map(({ value, attributes }) => ({
    name: ImageBlot.blotName,
    value,
    attributes,
  }));
}

interface UploaderOptions {
  onUploadError?: (errorMessage: string) => void;
}

class Uploader extends Module<UploaderOptions> {
  private activeDropTarget: Blot | null = null;

  private supportAttachment: boolean;
  private supportImage: boolean;

  private placeholder: HTMLDivElement | null = null;

  constructor(quill: Quill, options: UploaderOptions) {
    super(quill, options);

    this.supportAttachment = !!quill.scroll.query(Attachment.blotName);
    this.supportImage = !!quill.scroll.query(ImageBlot.blotName);
    if (this.supportAttachment || this.supportImage) {
      this.placeholder = document.createElement('div');
      this.placeholder.style.display = 'none';
      this.placeholder.classList.add(styles.dropPlaceholder);
      this.quill.addContainer(this.placeholder);

      this.setupListeners();
    }
  }

  private setupListeners() {
    const { quill } = this;

    quill.root.addEventListener('dragover', (e) => {
      if (!quill.scroll.isEnabled()) {
        return;
      }
      e.preventDefault();

      if (
        !quill.scroll.query('attachment') &&
        !Array.from(e.dataTransfer?.items ?? []).every(isEditorCompatibleImage)
      ) {
        return;
      }

      let { target } = e;
      if (!(target instanceof Element)) {
        return;
      }

      // When drag over the gap between elements (the margin area of element CSS boxes),
      // the target becomes the editor itself or a container blot (e.g. ListContainer).
      // This causes the placeholder jumps which we want to avoid.
      const blot = quill.scroll.find(target);
      if (blot === quill.scroll) {
        const { tail } = quill.scroll.children;
        if (!(tail?.domNode instanceof Element)) {
          return;
        }
        const { bottom } = tail.domNode.getBoundingClientRect();
        if (bottom < e.clientY) {
          target = tail.domNode;
        } else {
          return;
        }
      }
      if (blot instanceof Container) {
        return;
      }

      this.setActiveDropTarget(
        getLineBlotFromTarget(quill, {
          target: target as Node,
          clientY: e.clientY,
        }),
      );
    });

    quill.root.addEventListener('drop', (e) => {
      const target = this.activeDropTarget;
      if (!target) {
        return;
      }

      e.preventDefault();
      this.setActiveDropTarget(null);

      getRegularFiles(e.dataTransfer).then((regularFiles) => {
        if (!regularFiles.length) {
          return;
        }

        if (
          'appendAttachment' in target &&
          typeof target.appendAttachment === 'function'
        ) {
          target.appendAttachment(regularFiles);
        } else {
          this.handleUpload(
            { index: quill.getIndex(target) + target.length(), length: 0 },
            regularFiles,
          );
        }
      });
    });

    onDnDDragEnd(quill.root, () => {
      this.setActiveDropTarget(null);
    });
  }

  private setActiveDropTarget(next: DraggableBlot | null) {
    if (this.activeDropTarget === next) {
      return;
    }

    if (this.activeDropTarget && isBlotDroppable(this.activeDropTarget)) {
      this.activeDropTarget.togglePlaceholder(false);
    }

    this.activeDropTarget = next;

    if (next) {
      if (isBlotDroppable(next)) {
        next.togglePlaceholder(true);
        if (this.placeholder) {
          this.placeholder.style.display = 'none';
        }
      } else if (this.placeholder) {
        Object.assign(this.placeholder.style, {
          display: 'block',
          ...getDropPlaceholderPosition(this.quill, next),
        });
      }
    } else if (this.placeholder) {
      this.placeholder.style.display = 'none';
    }
  }

  public getActiveDropTarget() {
    return this.activeDropTarget;
  }

  public insertEmbeds(
    range: Range,
    embeds: {
      name: string;
      value: unknown;
      attributes?: Record<string, unknown>;
    }[],
  ) {
    const delta = new Delta().retain(range.index);
    embeds.forEach(({ name, value, attributes }) => {
      delta.insert({ [name]: value }, attributes);
    });

    this.quill.updateContents(delta, Quill.sources.USER);
    this.quill.setSelection(range.index + embeds.length, Quill.sources.SILENT);
  }

  async handleUpload(
    range: Range,
    filesOrImageURLs: (File | string)[],
    forceAttachment = false,
  ) {
    const { files = [], urls = [] } = _.groupBy(filesOrImageURLs, (item) =>
      item instanceof File ? 'files' : 'urls',
    ) as { files: File[]; urls: string[] };

    const shouldUploadAsAttachments =
      this.supportAttachment &&
      (forceAttachment ||
        !this.supportImage ||
        !files.every(isEditorCompatibleImage));

    const [overlargeFiles, validFiles] = _.partition(files, isFileOverlarge);
    const { onUploadError } = this.options;
    if (onUploadError) {
      overlargeFiles.forEach(() => {
        onUploadError(
          shouldUploadAsAttachments
            ? FILE_OVERSIZED_ERROR
            : IMAGE_OVERSIZED_ERROR,
        );
      });
    }

    if (validFiles.length && (this.supportImage || this.supportAttachment)) {
      if (shouldUploadAsAttachments) {
        this.insertEmbeds(range, [
          { name: Attachment.blotName, value: validFiles },
        ]);
        this.quill.setSelection(range.index, 1, Quill.sources.SILENT);
      } else {
        const embeds = await embedsFromImageFiles(validFiles);
        this.insertEmbeds(range, embeds);
      }
    }

    if (urls.length && this.supportImage) {
      const embeds = (await Promise.all(urls.map(getImagePlaceholderInfo))).map(
        ({ value, attributes }) => ({
          name: ImageBlot.blotName,
          value,
          attributes,
        }),
      );

      this.insertEmbeds(range, embeds);
    }
  }

  // This method is required by Clipboard module.
  upload(range: Range, files: File[]) {
    this.handleUpload(range, files);
  }
}

export default Uploader;
